import type {
  FicheEntretien,
  GrilleOuiNon,
  OptionFiche,
  ReferentielFiche,
  ReponseOuiNon,
} from '../../lib/useEntretiens';

/** Libellé d'un code du référentiel ; le code inconnu s'affiche tel quel. */
function libelle(options: OptionFiche[], code: string): string {
  if (code.trim() === '') return '—';
  return options.find((option) => option.code === code)?.libelle ?? code;
}

function libelles(options: OptionFiche[], codes: string[]): string {
  if (codes.length === 0) return '—';
  return codes.map((code) => libelle(options, code)).join(', ');
}

function reponse(valeur: ReponseOuiNon): string {
  if (valeur === 'OUI') return 'Oui';
  if (valeur === 'NON') return 'Non';
  return '—';
}

function valeur(texte: string): string {
  return texte.trim() === '' ? '—' : texte;
}

/** Date saisie en clair sur la fiche : civile, sans instant ni fuseau. */
function jour(saisi: string): string {
  const [annee, mois, jourDuMois] = saisi.split('-');
  return annee && mois && jourDuMois ? `${jourDuMois}/${mois}/${annee}` : '—';
}

function Ligne({ libelle: intitule, valeur: contenu }: { libelle: string; valeur: string }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
        {intitule}
      </div>
      <div className="mt-0.5 whitespace-pre-line break-words text-sm font-medium text-codialis-navy">
        {contenu}
      </div>
    </div>
  );
}

function Rubrique({ titre, children }: { titre: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-gray-100 pt-3">
      <h4 className="text-xs font-bold uppercase tracking-wider text-codialis-sky">{titre}</h4>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </div>
  );
}

/**
 * Grille oui/non du papier : une réponse par ligne. Les lignes sans réponse
 * sont absentes de la fiche — les afficher « non » ferait dire au candidat ce
 * qu'il n'a pas dit.
 */
function Grille({
  titre,
  options,
  reponses,
}: {
  titre: string;
  options: OptionFiche[];
  reponses: GrilleOuiNon;
}): JSX.Element {
  const dites = options.filter((option) => reponses[option.code] !== undefined);

  return (
    <div className="sm:col-span-2 lg:col-span-3 min-w-0">
      <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
        {titre}
      </div>
      {dites.length === 0 ? (
        <div className="mt-0.5 text-sm font-medium text-codialis-navy">—</div>
      ) : (
        <ul className="mt-1 grid grid-cols-1 gap-1 sm:grid-cols-2">
          {dites.map((option) => (
            <li key={option.code} className="flex items-baseline justify-between gap-2 text-sm">
              <span className="text-codialis-gray">{option.libelle}</span>
              <span className="font-medium text-codialis-navy">
                {reponses[option.code] === 'OUI' ? 'Oui' : 'Non'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Fiche de recrutement recueillie pendant l'entretien, en lecture seule dans
 * le dossier (CDC §4.1, §4.3).
 *
 * Le document papier tient sur sept pages : tout y est repris, rubrique par
 * rubrique et dans le même ordre, parce que le dossier est le point d'entrée
 * du suivi du candidat — ce qui a été dit à l'entretien ne doit pas rester
 * dans le seul tableau du pôle commercial.
 *
 * L'identité n'y est pas répétée : elle est déjà en tête de la fiche du
 * dossier, et la reprendre ici ferait relire deux fois la même chose. Seules
 * les rubriques d'identité propres à l'entretien — pièce d'identité, carte de
 * séjour — sont affichées.
 */
export function FicheEntretienLecture({
  fiche,
  referentiel,
}: {
  fiche: FicheEntretien;
  referentiel: ReferentielFiche;
}): JSX.Element {
  return (
    <div className="space-y-3">
      <Rubrique titre="Origine de la candidature">
        <Ligne libelle="Orienté par" valeur={libelle(referentiel.orienteurs, fiche.orientePar)} />
        <Ligne libelle="Précision" valeur={valeur(fiche.orienteParPrecision)} />
        <Ligne libelle="Formation demandée" valeur={valeur(fiche.formationDemandee)} />
      </Rubrique>

      {/* L'identité est reprise du dossier à l'ouverture de la fiche, mais une
          correction faite pendant l'entretien reste sur la fiche : ce qui est
          affiché ici est ce que le candidat a déclaré, qui peut différer de
          l'état civil du dossier plus haut. */}
      <Rubrique titre="Identité déclarée à l’entretien">
        <Ligne libelle="Nom de naissance" valeur={valeur(fiche.identite.nomNaissance)} />
        <Ligne libelle="Prénoms" valeur={valeur(fiche.identite.prenoms)} />
        <Ligne libelle="Nom d’usage" valeur={valeur(fiche.identite.nomUsage)} />
        <Ligne libelle="Date de naissance" valeur={jour(fiche.identite.dateNaissance)} />
        <Ligne libelle="Commune de naissance" valeur={valeur(fiche.identite.communeNaissance)} />
        <Ligne libelle="Nationalité" valeur={valeur(fiche.identite.nationalite)} />
        <Ligne libelle="Téléphone" valeur={valeur(fiche.identite.telephone)} />
        <Ligne libelle="Courriel" valeur={valeur(fiche.identite.mail)} />
        <Ligne libelle="Adresse" valeur={valeur(fiche.identite.adresse)} />
        <Ligne libelle="Code postal" valeur={valeur(fiche.identite.codePostal)} />
        <Ligne libelle="Commune" valeur={valeur(fiche.identite.ville)} />
        <Ligne libelle="Pièce d’identité" valeur={valeur(fiche.identite.pieceIdentite)} />
        <Ligne libelle="Carte de séjour" valeur={valeur(fiche.identite.carteSejour)} />
      </Rubrique>

      <Rubrique titre="Logement">
        <Ligne libelle="Statut" valeur={libelle(referentiel.logements, fiche.logement.statut)} />
        <Ligne libelle="Précision" valeur={valeur(fiche.logement.autre)} />
      </Rubrique>

      <Rubrique titre="Situation familiale">
        <Ligne
          libelle="Statut"
          valeur={libelle(referentiel.situationsFamiliales, fiche.familiale.statut)}
        />
        <Ligne libelle="Nombre d’enfants" valeur={valeur(fiche.familiale.nombreEnfants)} />
        <Ligne libelle="Âges des enfants" valeur={valeur(fiche.familiale.agesEnfants)} />
        <Ligne libelle="Contact d’urgence" valeur={valeur(fiche.familiale.contactUrgence)} />
        <Ligne libelle="Responsable légal" valeur={valeur(fiche.familiale.responsableLegal)} />
        <Ligne libelle="Suivi judiciaire" valeur={reponse(fiche.familiale.suiviJustice)} />
      </Rubrique>

      <Rubrique titre="Situation actuelle">
        <Ligne
          libelle="Statuts"
          valeur={libelles(referentiel.situationsActuelles, fiche.situation.statuts)}
        />
        <Ligne libelle="Précision" valeur={valeur(fiche.situation.autre)} />
        <Ligne libelle="Reconnaissance RQTH" valeur={reponse(fiche.situation.rqth)} />
        {fiche.situation.rqth === 'NON' && (
          <>
            <Ligne
              libelle="Aide au dossier RQTH"
              valeur={reponse(fiche.situation.aideDossierRqth)}
            />
            <Ligne
              libelle="Commentaire RQTH"
              valeur={valeur(fiche.situation.aideDossierRqthCommentaire)}
            />
          </>
        )}
      </Rubrique>

      <Rubrique titre="Mobilité">
        <Ligne libelle="Permis" valeur={libelle(referentiel.etatsPermis, fiche.mobilite.permis)} />
        <Ligne libelle="Précision" valeur={valeur(fiche.mobilite.permisPrecision)} />
        <Ligne
          libelle="Moyens de locomotion"
          valeur={libelles(referentiel.moyensLocomotion, fiche.mobilite.locomotion)}
        />
        <Ligne libelle="Autre moyen" valeur={valeur(fiche.mobilite.locomotionAutre)} />
        <Ligne
          libelle="Zone de déplacement"
          valeur={valeur(fiche.mobilite.zoneDeplacement)}
        />
      </Rubrique>

      <Rubrique titre="Ressources">
        <Ligne
          libelle="Allocations CAF"
          valeur={libelles(referentiel.allocationsCaf, fiche.ressources.allocationsCaf)}
        />
        <Ligne libelle="Autre allocation" valeur={valeur(fiche.ressources.allocationAutre)} />
        <Ligne libelle="Inscrit à France Travail" valeur={reponse(fiche.ressources.franceTravail)} />
        <Ligne
          libelle="Aides France Travail"
          valeur={libelles(referentiel.aidesFranceTravail, fiche.ressources.aidesFranceTravail)}
        />
        <Ligne libelle="Autre aide" valeur={valeur(fiche.ressources.aideAutre)} />
        {fiche.ressources.franceTravail === 'OUI' && (
          <>
            <Ligne
              libelle="Conseiller France Travail"
              valeur={valeur(fiche.ressources.conseillerFranceTravail)}
            />
            <Ligne
              libelle="Identifiant France Travail"
              valeur={valeur(fiche.ressources.identifiantFranceTravail)}
            />
          </>
        )}
        <Ligne libelle="Mission locale" valeur={reponse(fiche.ressources.missionLocale)} />
        <Ligne
          libelle="Conseiller mission locale"
          valeur={valeur(fiche.ressources.conseillerMissionLocale)}
        />
        <Ligne libelle="AAH" valeur={reponse(fiche.ressources.aah)} />
        <Ligne
          libelle="Couverture sociale"
          valeur={libelles(referentiel.couverturesSociales, fiche.ressources.couvertureSociale)}
        />
      </Rubrique>

      <Rubrique titre="Parcours de formation">
        <Ligne libelle="Niveau de formation" valeur={valeur(fiche.parcours.niveauFormation)} />
        <Ligne libelle="Dernier diplôme" valeur={valeur(fiche.parcours.dernierDiplome)} />
        <Ligne libelle="Déjà inscrit en CFA" valeur={reponse(fiche.parcours.inscritCfa)} />
        <Ligne libelle="Lequel" valeur={valeur(fiche.parcours.cfaLequel)} />
        <div className="sm:col-span-2 lg:col-span-3 min-w-0">
          <Ligne
            libelle="Dernière formation suivie"
            valeur={valeur(fiche.parcours.derniereFormation)}
          />
        </div>
      </Rubrique>

      <Rubrique titre="Expériences professionnelles">
        <Ligne libelle="Dernier emploi" valeur={valeur(fiche.experiences.dernierEmploi)} />
        <Ligne
          libelle="A déjà travaillé dans le domaine"
          valeur={reponse(fiche.experiences.dejaTravailleDomaine)}
        />
        <Ligne libelle="Entreprise" valeur={valeur(fiche.experiences.entreprise)} />
        <Ligne libelle="Fonction" valeur={valeur(fiche.experiences.fonction)} />
        <Ligne libelle="Durée" valeur={valeur(fiche.experiences.duree)} />
        <div className="sm:col-span-2 lg:col-span-3 min-w-0">
          <Ligne libelle="Missions" valeur={valeur(fiche.experiences.missions)} />
        </div>
      </Rubrique>

      <Rubrique titre="Projet professionnel">
        <Ligne libelle="Poste recherché" valeur={valeur(fiche.projet.posteRecherche)} />
        <Ligne
          libelle="Objectifs de la formation"
          valeur={libelles(referentiel.objectifsFormation, fiche.projet.objectifs)}
        />
        <Ligne libelle="Autre objectif" valeur={valeur(fiche.projet.objectifAutre)} />
        <div className="sm:col-span-2 lg:col-span-3 min-w-0">
          <Ligne libelle="Motivation" valeur={valeur(fiche.projet.motivation)} />
        </div>
      </Rubrique>

      <Rubrique titre="Compétences">
        <Ligne libelle="CV à jour" valeur={reponse(fiche.competences.cvAJour)} />
        <Ligne libelle="Points forts identifiés" valeur={reponse(fiche.competences.pointsForts)} />
        <Ligne libelle="Lesquels" valeur={valeur(fiche.competences.pointsFortsPrecision)} />
        <Grille
          titre="Savoirs de base"
          options={referentiel.savoirsBase}
          reponses={fiche.competences.savoirs}
        />
        <Grille
          titre="Outils numériques"
          options={referentiel.outilsNumeriques}
          reponses={fiche.competences.numeriques}
        />
        <div className="sm:col-span-2 lg:col-span-3 min-w-0">
          <Ligne libelle="Commentaires" valeur={valeur(fiche.competences.commentaires)} />
        </div>
      </Rubrique>

      <Rubrique titre="Situation de handicap">
        <Ligne libelle="Situation déclarée" valeur={reponse(fiche.handicap.situation)} />
        <Ligne
          libelle="Souhaite rencontrer le référent"
          valeur={reponse(fiche.handicap.rencontrerReferent)}
        />
        <div className="sm:col-span-2 lg:col-span-3 min-w-0">
          <Ligne libelle="Besoins d’aménagement" valeur={valeur(fiche.handicap.besoins)} />
        </div>
      </Rubrique>

      <Rubrique titre="Disponibilités et contraintes">
        <Ligne
          libelle="Disponibilité"
          valeur={libelle(referentiel.disponibilites, fiche.disponibilites.disponibilite)}
        />
        <Ligne
          libelle="Disponible sur toute la durée de la formation"
          valeur={reponse(fiche.disponibilites.dureeFormation)}
        />
        <Ligne
          libelle="Enquête métier réalisée"
          valeur={reponse(fiche.disponibilites.enqueteMetier)}
        />
        <Ligne libelle="Précisions" valeur={valeur(fiche.disponibilites.enquetePrecision)} />
        <Grille
          titre="Conditions de travail acceptées"
          options={referentiel.conditionsTravail}
          reponses={fiche.disponibilites.conditionsTravail}
        />
        <Grille
          titre="Contraintes pouvant gêner la formation"
          options={referentiel.contraintesImpact}
          reponses={fiche.disponibilites.contraintesImpact}
        />
        <div className="sm:col-span-2 lg:col-span-3 min-w-0">
          <Ligne
            libelle="Contraintes particulières"
            valeur={valeur(fiche.disponibilites.contraintesParticulieres)}
          />
        </div>
        <div className="sm:col-span-2 lg:col-span-3 min-w-0">
          <Ligne
            libelle="Précisions sur les contraintes"
            valeur={valeur(fiche.disponibilites.precisionsContraintes)}
          />
        </div>
        <div className="sm:col-span-2 lg:col-span-3 min-w-0">
          <Ligne
            libelle="Autres remarques"
            valeur={valeur(fiche.disponibilites.autresRemarques)}
          />
        </div>
      </Rubrique>

      <Rubrique titre="Conclusion de l’entretien">
        <div className="sm:col-span-2 lg:col-span-3 min-w-0">
          <Ligne libelle="Conclusion" valeur={valeur(fiche.conclusion)} />
        </div>
      </Rubrique>
    </div>
  );
}
